/**
 * useCommandProcessor Hook
 * Processes tool execution requests from the AI assistant
 * Executes commands using the pipeline builder store
 */

import { useCallback } from 'react';
import { usePipelineBuilderStore } from '../../../store/pipelineBuilderStore';
import { ToolCall } from '../types';
import { Node } from 'reactflow';
import { autoLayoutNodes } from '../../../utils/autoLayout';
import apiService from '../../../services/api';

interface CommandResult {
  success: boolean;
  result?: any;
  error?: string;
}

export function useCommandProcessor() {
  // Get store actions
  const store = usePipelineBuilderStore();

  /**
   * Execute a tool call from the AI assistant
   */
  const executeToolCall = useCallback(
    async (toolCall: ToolCall): Promise<CommandResult> => {
      const { tool_name, tool_params } = toolCall;

      try {
        switch (tool_name) {
          case 'get_pipeline': {
            // Get full pipeline from database
            // Use compact=true for AI Assistant to get migrated prompts
            const { pipeline_id } = tool_params;

            try {
              const pipeline = await apiService.getPipeline(pipeline_id);

              return {
                success: true,
                result: {
                  id: pipeline.id,
                  name: pipeline.name,
                  description: pipeline.description,
                  version: pipeline.version,
                  status: pipeline.status,
                  steps: pipeline.steps
                }
              };
            } catch (error) {
              return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to get pipeline'
              };
            }
          }

          case 'update_pipeline': {
            // Update pipeline in database
            const { pipeline_id, steps, name, description, version, status } = tool_params;

            try {
              // Build update request (only include provided fields)
              const updateData: any = {};
              if (steps !== undefined) updateData.steps = steps;
              if (name !== undefined) updateData.name = name;
              if (description !== undefined) updateData.description = description;
              if (version !== undefined) updateData.version = version;
              if (status !== undefined) updateData.status = status;

              const updated = await apiService.updatePipeline(pipeline_id, updateData);

              // Reload pipeline in UI from updated data
              await store.loadPipeline(updated);

              return {
                success: true,
                result: {
                  id: updated.id,
                  name: updated.name,
                  description: updated.description,
                  version: updated.version,
                  status: updated.status,
                  steps: updated.steps,
                  message: 'Pipeline updated successfully'
                }
              };
            } catch (error) {
              return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to update pipeline'
              };
            }
          }

          case 'validate_pipeline': {
            // Validate pipeline structure
            const { steps, name } = tool_params;

            try {
              const results = await apiService.validatePipeline({ steps, name });

              return {
                success: true,
                result: {
                  is_valid: results.is_valid,
                  errors: results.errors || [],
                  warnings: results.warnings || [],
                  message: results.is_valid
                    ? 'Pipeline is valid'
                    : `Pipeline has ${results.errors?.length || 0} error(s)`
                }
              };
            } catch (error) {
              return {
                success: false,
                error: error instanceof Error ? error.message : 'Validation failed'
              };
            }
          }

          case 'get_processors': {
            // Get list of available processors
            try {
              const processors = await apiService.getProcessors();

              return {
                success: true,
                result: processors.map(p => ({
                  processor_name: p.processor_name,
                  display_name: p.display_name,
                  processor_type: p.processor_type,
                  description: p.description,
                  required_input_keys: p.required_input_keys,
                  optional_input_keys: p.optional_input_keys,
                  output_keys: p.output_keys,
                  default_config: p.default_config
                }))
              };
            } catch (error) {
              return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to get processors'
              };
            }
          }

          case 'auto_layout': {
            // Calculate optimal positions for steps
            const { steps } = tool_params;

            try {
              // We need to temporarily convert steps to nodes/edges for layout calculation
              // First, load the steps into the store temporarily
              const currentPipeline = {
                id: store.pipelineId || 0,
                name: store.pipelineName || 'Pipeline',
                description: store.pipelineDescription || '',
                version: '1.0.0',
                status: 'DRAFT' as const,
                steps: steps,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                created_by: null,
                workflow_count: 0
              };

              // Load pipeline to get nodes/edges
              await store.loadPipeline(currentPipeline);

              // Get the generated nodes and edges
              const { nodes, edges } = store;
              const layoutedNodes = autoLayoutNodes(nodes, edges);

              // Build map: step_id → position
              const positionMap = new Map<string, {x: number, y: number}>();
              layoutedNodes.forEach((node: Node) => {
                const stepId = node.data.step_id || node.id;
                positionMap.set(stepId, node.position);
              });

              // Update ui_position in steps array
              const layoutedSteps = steps.map((step: any) => ({
                ...step,
                ui_position: positionMap.get(step.step_id) || step.ui_position || {x: 0, y: 0}
              }));

              return {
                success: true,
                result: layoutedSteps
              };
            } catch (error) {
              return {
                success: false,
                error: error instanceof Error ? error.message : 'Auto-layout failed'
              };
            }
          }

          default:
            return {
              success: false,
              error: `Unknown tool: ${tool_name}`,
            };
        }
      } catch (error) {
        console.error(`Error executing tool ${tool_name}:`, error);
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [store]
  );

  return {
    executeToolCall,
  };
}
